/* Modal genérico para registrar el resultado de tareas de agente post-visita:
   T4b2/T4b3 (WhatsApp descarte/objeciones), T7 (cierre/reserva).
   T4b1 no usa este modal: Telnyx y WhatsApp detectan el contacto y abren M2. */

import { useState } from "react";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { FLOW_OUTCOMES, parseTaskMetadata } from "../constants/leadFlowOutcomes";
import { getTaskDisplayCode } from "../constants/taskUiColors";
import usePermiso from "../hooks/usePermiso";

/** Permiso granular (t_agente_*) de cada opción de resultado del modal de agente. */
const AGENT_OUTCOME_NODE_ID = {
  enviado: "t_agente_wa_enviado",
  reserva_confirmada: "t_agente_reservado",
  operacion_cerrada: "t_agente_cerrado",
};

const COLORES = {
  contactado:        { bg: "#16a34a", text: "#fff" },
  no_contactado:     { bg: "#dc2626", text: "#fff" },
  enviado:           { bg: "#2563eb", text: "#fff" },
  reserva_confirmada:{ bg: "#16a34a", text: "#fff" },
  operacion_cerrada: { bg: "#7c3aed", text: "#fff" },
  default:           { bg: "#475569", text: "#fff" },
};

function colorBoton(codigo) {
  return COLORES[codigo] || COLORES.default;
}

export const TASK_CODES_AGENTE_RESULTADO = new Set(["T4B2", "T4B3", "T7"]);

/** Devuelve true si la tarea es de agente post-visita (T4b2, T4b3, T7). */
export function isAgenteResultTask(tarea = {}) {
  if (!tarea?.id) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const meta = parseTaskMetadata(tarea);
  return TASK_CODES_AGENTE_RESULTADO.has(String(meta.task_code || "").toUpperCase());
}

export default function ModalResultadoTareaAgente({
  isOpen,
  tarea,
  onClose,
  onCompleted,
  theme,
}) {
  const c = theme?.colors || {};
  const isDark = theme?.name === "dark";
  const { puede } = usePermiso();
  const [guardando, setGuardando] = useState(null);

  if (!isOpen || !tarea?.id) return null;

  const meta = parseTaskMetadata(tarea);
  const taskCode = String(meta.task_code || "").toUpperCase();
  const displayCode = getTaskDisplayCode(tarea) || taskCode;
  const label = meta.task_label || taskCode;
  // Cada opción es un nodo t_agente_* independiente: se oculta si no tiene "ver"
  // (por defecto todos son permisivos, sin cambio de comportamiento).
  const outcomesBase = FLOW_OUTCOMES[taskCode] || [];
  const outcomes = outcomesBase.filter((op) => {
    const nodeId = AGENT_OUTCOME_NODE_ID[op.codigo];
    return !nodeId || puede(nodeId, "ver");
  });
  const leadId = tarea.lead_id;

  async function completar(outcome) {
    if (guardando) return;
    setGuardando(outcome.codigo);
    try {
      await api.patch(`/tareas/${tarea.id}`, {
        estado: "completada",
        resultado: outcome.resultado,
        resultado_codigo: outcome.codigo,
      });
      await onCompleted?.(outcome);
      onClose?.();
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "Error al guardar el resultado");
    } finally {
      setGuardando(null);
    }
  }

  const overlay = {
    position: "fixed",
    inset: 0,
    zIndex: 1300,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const panel = {
    background: isDark ? "#1e293b" : "#fff",
    borderRadius: 16,
    padding: "24px 24px 20px",
    width: "100%",
    maxWidth: 420,
    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
    boxShadow: isDark ? "0 24px 64px rgba(0,0,0,0.6)" : "0 20px 48px rgba(15,23,42,0.18)",
  };

  return (
    <div style={overlay} onClick={() => !guardando && onClose?.()}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>
          {displayCode}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#f1f5f9" : "#0f172a", lineHeight: 1.2, marginBottom: 16 }}>
          {label}
        </div>

        {outcomes.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {outcomes.map((op) => {
              const col = colorBoton(op.codigo);
              const cargando = guardando === op.codigo;
              return (
                <button
                  key={op.codigo}
                  onClick={() => completar(op)}
                  disabled={!!guardando}
                  style={{
                    padding: "13px 20px",
                    borderRadius: 10,
                    border: "none",
                    background: col.bg,
                    color: col.text,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: guardando ? "not-allowed" : "pointer",
                    opacity: guardando && !cargando ? 0.5 : 1,
                    transition: "opacity 0.12s",
                  }}
                >
                  {cargando ? "Guardando…" : op.label}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: 14 }}>
            Sin opciones definidas para esta tarea.
          </div>
        )}

        <button
          onClick={() => !guardando && onClose?.()}
          disabled={!!guardando}
          style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: isDark ? "#64748b" : "#94a3b8", fontSize: 13, cursor: guardando ? "not-allowed" : "pointer" }}
        >
          Ahora no
        </button>
      </div>
    </div>
  );
}
